'use client';
import React, { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setInView(true);},
      { threshold }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const services = [
{
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_132814dda-1783971080707.png",
  title: 'Médecine Générale',
  desc: 'Spécialité clé du centre, la médecine générale répond à 80% des demandes de consultation avec des praticiens expérimentés.'
},
{
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_19a532aac-1765225829726.png",
  title: 'Médecine Spécialisée',
  desc: 'Pédiatrie, gynécologie, diabétologie, ophtalmologie, fibroscopie et ORL — des spécialistes pour chaque besoin.'
},
{
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3fec79c-1785328107245.png",
  title: 'Télémédecine',
  desc: 'Diagnostics à distance grâce à des outils spécialisés et des professionnels qualifiés, pour un accès facilité aux soins.'
},
{
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9fd5f4b-1785328106689.png",
  title: 'Analyses Biomédicales',
  desc: 'Analyses biomédicales de haute précision réalisées en partenariat avec le Laboratoire Montagnier, référence depuis 1954.'
}];


export default function OurServices() {
  const { ref, inView } = useInView();

  return (
    <div className="our-services">
      <div className="dentaire-container">
        {/* Section Title */}
        <div className="row section-row">
          <div className="col-lg-12">
            <div className="section-title" style={{ textAlign: 'center', marginBottom: 0 }}>
              <h3>nos services</h3>
              <h2><span>Des Soins de Qualité</span> Accessibles à Tous.</h2>
              <p>COSA offre une gamme complète de services médicaux et paramédicaux pour toute la famille.</p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="row" ref={ref}>
          {services?.map((service, i) =>
          <div key={service?.title} className="col-lg-3 col-md-6">
              <div
              className="service-item"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(30px)',
                transition: `opacity 0.6s ease ${i * 0.25}s, transform 0.6s ease ${i * 0.25}s`
              }}>
              
                <div className="icon-box">
                  <div className="img">
                    <img src={service?.icon} alt={service?.title} />
                  </div>
                </div>
                <div className="service-body">
                  <h3>{service?.title}</h3>
                  <p>{service?.desc}</p>
                </div>
                <div className="read-more-btn">
                  <a href="#">en savoir plus</a>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="col-lg-12">
            <div
              className="services-box-footer"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.6s ease 1s, transform 0.6s ease 1s'
              }}>
              
              <p>Nous intégrons les dernières technologies médicales pour garantir les meilleurs résultats à nos patients.</p>
              <a href="#" className="btn-default">voir tous les services</a>
            </div>
          </div>
        </div>
      </div>
      {/* Video Section */}
      <div className="intro-clinic-video">
        <div style={{ width: '100%', maxWidth: '1600px', margin: '0 auto', padding: '0 15px' }}>
          <div className="row">
            <div className="col-lg-12">
              <div
                className="visit-clinic"
                style={{
                  backgroundImage: 'url(/assets/images/visit-clinic-bg.png)',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center'
                }}>
                
                <div className="visit-clinic-content">
                  <div className="section-title">
                    <h3>visitez notre centre</h3>
                    <h2>Des Soins Médicaux Complets Pour Toute la Famille</h2>
                  </div>
                  <div className="visit-clinic-btn">
                    <a
                      href="https://www.youtube.com/watch?v=Y-x0efG1seA"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="play-btn">
                      
                      voir la vidéo
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Counter Section */}
      <CounterSection />
      {/* Star Icon */}
      <div className="icon-star-image">
        <img src="https://html.awaikenthemes.com/dentaire/images/icon-star.svg" alt="Star decoration" />
      </div>
    </div>);

}

function CounterSection() {
  const { ref, inView } = useInView();
  const [counts, setCounts] = useState({ c1: 0, c2: 0, c3: 0, c4: 0 });
  const animated = useRef(false);

  useEffect(() => {
    if (inView && !animated?.current) {
      animated.current = true;
      const targets = { c1: 70, c2: 8, c3: 12, c4: 7 };
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        setCounts({
          c1: Math.round(targets?.c1 * progress),
          c2: Math.round(targets?.c2 * progress),
          c3: Math.round(targets?.c3 * progress),
          c4: Math.round(targets?.c4 * progress)
        });
        if (step >= steps) clearInterval(timer);
      }, interval);
    }
  }, [inView]);

  const items = [
  { count: counts?.c1, suffix: '+', label: 'années d\'expérience' },
  { count: counts?.c2, suffix: '', label: 'spécialités médicales' },
  { count: counts?.c3, suffix: 'K', label: 'patients satisfaits' },
  { count: counts?.c4, suffix: '+', label: 'médecins spécialistes' }];


  return (
    <div className="our-counter">
      <div className="dentaire-container">
        <div className="row" ref={ref}>
          {items?.map((item, i) =>
          <div key={item?.label} className="col-lg-3 col-md-6">
              <div className="counter-item">
                <div className="counter-title">
                  <h2>
                    <span className="counter">
                      {i === 1 ? String(item?.count)?.padStart(2, '0') : item?.count}
                    </span>
                    {item?.suffix}
                  </h2>
                  <h3>{item?.label}</h3>
                </div>
                <div className="counter-content">
                  <p>COSA, votre partenaire santé de confiance depuis 1954 en Côte d'Ivoire.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

}